/**
 * Customer Authentication Service
 * Business logic wrapper for auth operations
 * Handles localStorage management, token validation, etc.
 */

import {
  CUSTOMER_AUTH_STORAGE_KEYS,
  CUSTOMER_SESSION_STORAGE_KEYS,
  CustomerProfileDto,
  DecodedTokenClaims,
} from "../types/customer-auth.types";

/**
 * Safe storage wrapper — falls back to sessionStorage when
 * localStorage is blocked (e.g. Brave Shields, Safari ITP).
 */
export const safeLocalStorage = {
  getItem(key: string): string | null {
    try {
      const localValue = localStorage.getItem(key);
      if (localValue !== null) {
        return localValue;
      }
    } catch {
      // ignore and fall back to sessionStorage
    }
    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  },
  setItem(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch {
      try {
        sessionStorage.setItem(key, value);
      } catch {
        /* storage unavailable — silently ignore */
      }
    }
  },
  removeItem(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch { /* ignore */ }
    try {
      sessionStorage.removeItem(key);
    } catch { /* ignore */ }
  },
};

/**
 * Store authentication data to localStorage
 */
export const storeAuthData = (
  token: string,
  customerData: CustomerProfileDto,
  qrToken?: string
): void => {
  safeLocalStorage.setItem(CUSTOMER_AUTH_STORAGE_KEYS.TOKEN, token);
  safeLocalStorage.setItem(
    CUSTOMER_AUTH_STORAGE_KEYS.CUSTOMER_ID,
    customerData.id
  );
  safeLocalStorage.setItem(
    CUSTOMER_AUTH_STORAGE_KEYS.PHONE_NUMBER,
    customerData.phoneNumber
  );
  safeLocalStorage.setItem(
    CUSTOMER_AUTH_STORAGE_KEYS.RESTAURANT_ID,
    String(customerData.restaurantId)
  );
  safeLocalStorage.setItem(
    CUSTOMER_AUTH_STORAGE_KEYS.FULL_NAME,
    customerData.fullName
  );
  safeLocalStorage.setItem(CUSTOMER_AUTH_STORAGE_KEYS.EMAIL, customerData.email);

  if (customerData.profilePictureUrl) {
    safeLocalStorage.setItem(
      CUSTOMER_AUTH_STORAGE_KEYS.PROFILE_PICTURE,
      customerData.profilePictureUrl
    );
  }
  
  // Store QR token to associate auth with specific QR code
  if (qrToken) {
    safeLocalStorage.setItem(CUSTOMER_AUTH_STORAGE_KEYS.QR_TOKEN, qrToken);
  }
};

/**
 * Retrieve authentication data from localStorage
 */
export const retrieveAuthData = (): {
  token: string | null;
  customerId: string | null;
  phoneNumber: string | null;
  restaurantId: number | null;
  fullName: string | null;
  email: string | null;
  profilePictureUrl: string | null;
  qrToken: string | null;
} => {
  return {
    token: safeLocalStorage.getItem(CUSTOMER_AUTH_STORAGE_KEYS.TOKEN),
    customerId: safeLocalStorage.getItem(CUSTOMER_AUTH_STORAGE_KEYS.CUSTOMER_ID),
    phoneNumber: safeLocalStorage.getItem(
      CUSTOMER_AUTH_STORAGE_KEYS.PHONE_NUMBER
    ),
    restaurantId: safeLocalStorage.getItem(
      CUSTOMER_AUTH_STORAGE_KEYS.RESTAURANT_ID
    )
      ? Number(
          safeLocalStorage.getItem(CUSTOMER_AUTH_STORAGE_KEYS.RESTAURANT_ID)
        )
      : null,
    fullName: safeLocalStorage.getItem(CUSTOMER_AUTH_STORAGE_KEYS.FULL_NAME),
    email: safeLocalStorage.getItem(CUSTOMER_AUTH_STORAGE_KEYS.EMAIL),
    profilePictureUrl: safeLocalStorage.getItem(
      CUSTOMER_AUTH_STORAGE_KEYS.PROFILE_PICTURE
    ),
    qrToken: safeLocalStorage.getItem(CUSTOMER_AUTH_STORAGE_KEYS.QR_TOKEN),
  };
};

/**
 * Clear all authentication data from localStorage
 */
export const clearAuthData = (): void => {
  Object.values(CUSTOMER_AUTH_STORAGE_KEYS).forEach((key) => {
    safeLocalStorage.removeItem(key);
  });
};

/**
 * Clear cart from sessionStorage
 */
export const clearCart = (): void => {
  Object.values(CUSTOMER_SESSION_STORAGE_KEYS).forEach((key) => {
    sessionStorage.removeItem(key);
  });
};

/**
 * Validate JWT token format and expiration
 * Returns decoded claims if valid, null if invalid
 */
export const validateToken = (token: string): DecodedTokenClaims | null => {
  try {
    if (!token) return null;

    // Split JWT into parts
    const parts = token.split(".");
    if (parts.length !== 3) return null;

    // Decode payload (middle part)
    const decoded = JSON.parse(atob(parts[1]));

    // Check expiration
    const now = Math.floor(Date.now() / 1000);
    if (decoded.exp && decoded.exp < now) {
      return null; // Token expired
    }

    return decoded as DecodedTokenClaims;
  } catch (error) {
    return null; // Invalid token format
  }
};

/**
 * Check if token is valid and not expired
 */
export const isTokenValid = (token: string): boolean => {
  return validateToken(token) !== null;
};

/**
 * Extract customer ID from token claims
 */
export const getCustomerIdFromToken = (token: string): string | null => {
  const decoded = validateToken(token);
  return decoded?.CustomerAuthId || null;
};

/**
 * Extract restaurant ID from token claims
 */
export const getRestaurantIdFromToken = (token: string): number | null => {
  const decoded = validateToken(token);
  return decoded?.RestaurantId || null;
};

/**
 * Extract phone number from token claims
 */
export const getPhoneNumberFromToken = (token: string): string | null => {
  const decoded = validateToken(token);
  return decoded?.PhoneNumber || null;
};

/**
 * Get time remaining until token expiration (in seconds)
 */
export const getTokenExpirationTime = (token: string): number | null => {
  const decoded = validateToken(token);
  if (!decoded?.exp) return null;

  const now = Math.floor(Date.now() / 1000);
  const remaining = decoded.exp - now;

  return remaining > 0 ? remaining : null;
};

/**
 * Validate phone number format
 * Accepts 10+ digits (Indian and international formats)
 */
export const validatePhoneNumber = (phoneNumber: string): boolean => {
  // Remove all non-numeric characters
  const digitsOnly = phoneNumber.replace(/\D/g, "");
  // Check if at least 10 digits
  return digitsOnly.length >= 10;
};

/**
 * Validate email format
 */
export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate password strength (min 6 characters)
 */
export const validatePassword = (password: string): boolean => {
  return !!(password && password.length >= 6);
};

/**
 * Format phone number for display
 * e.g., "919278291908" -> "+91 9278291908"
 */
export const formatPhoneNumberForDisplay = (phoneNumber: string): string => {
  const digitsOnly = phoneNumber.replace(/\D/g, "");

  if (digitsOnly.length === 10) {
    // Indian format
    return `${digitsOnly.slice(0, 5)} ${digitsOnly.slice(5)}`;
  } else if (digitsOnly.length === 12) {
    // International format with country code
    return `+${digitsOnly.slice(0, 2)} ${digitsOnly.slice(2, 7)} ${digitsOnly.slice(7)}`;
  }

  return phoneNumber;
};

/**
 * Get initials from full name for avatar
 */
export const getInitialsFromName = (fullName: string): string => {
  if (!fullName) return "?";

  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0].substring(0, 1).toUpperCase();
  }

  return (parts[0].substring(0, 1) + parts[parts.length - 1].substring(0, 1))
    .toUpperCase();
};

export const customerAuthService = {
  storeAuthData,
  retrieveAuthData,
  clearAuthData,
  clearCart,
  validateToken,
  isTokenValid,
  getCustomerIdFromToken,
  getRestaurantIdFromToken,
  getPhoneNumberFromToken,
  getTokenExpirationTime,
  validatePhoneNumber,
  validateEmail,
  validatePassword,
  formatPhoneNumberForDisplay,
  getInitialsFromName,
};
